/**
 * Top-level error boundary for the SPA.
 *
 * Catches render-time exceptions in the route tree and shows a recoverable
 * UI instead of leaving the user staring at a blank page. The error itself is
 * logged to the console + sent to any onError sink the caller wires up
 * (Sentry, Cloudflare Analytics, etc.).
 */

import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional sink for error telemetry (e.g. Sentry.captureException). */
  onError?: (error: Error, info: { componentStack: string }) => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: { componentStack: string }): void {
    console.error('[ErrorBoundary] caught', error, info.componentStack)
    this.props.onError?.(error, info)
  }

  reset = () => {
    this.setState({ error: null })
  }

  hardReload = () => {
    window.location.reload()
  }

  override render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-muted px-6">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface shadow-sm p-8 text-center">
          <h1 className="text-lg font-semibold text-fg">Something broke</h1>
          <p className="mt-2 text-sm text-fg-muted">
            We hit an unexpected error rendering this page. Try again, or
            reload the app if the problem persists.
          </p>
          <pre className="mt-4 max-h-32 overflow-auto rounded-md border border-border bg-surface-muted px-3 py-2 text-left text-xs text-fg-subtle whitespace-pre-wrap break-words">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border border-border bg-surface text-fg px-3 py-2 text-sm hover:bg-surface-subtle"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.hardReload}
              className="rounded-md bg-[var(--color-primary)] text-white px-3 py-2 text-sm hover:bg-[var(--color-primary-hover)]"
            >
              Reload app
            </button>
          </div>
        </div>
      </div>
    )
  }
}
